import React, { useState, useRef, useEffect, useCallback } from 'react'
import { FormPdfCapture } from '@components/pages/FormBuilder/Shared/FormPdfCapture'
import type { SchemaFormFieldValues } from '@components/pages/FormBuilder/Shared/SchemaFormRenderer'
import { FormTemplateStatus } from '@dataTypes/forms.type'
import type {
  TemplateCategory,
  SelectedTemplateItem,
  GroupField,
  FormField,
  FormRowField,
  FormBuilderSelection,
  FormComponentType,
  SavedTemplateFormData,
  FieldSettingsUpdate,
  FieldSettingsUpdateHandler,
} from '../types'
import GroupFields from '../GroupFields'
import FormPreview from './FormPreview.component'
import { getDefaultGroupsForTemplate } from '../newPatientFormDefaults'
import { createFormField } from '../createFormField'
import { logger } from '@utils/safeLogger'
import './FormBuilderContent.css'

const createFormBuilderId = (prefix: string): string =>
  `${prefix}-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`}`

/** A dragged-out field leaves an empty standalone block. Drop those shells. */
const withoutEmptyStandaloneGroups = (groupList: GroupField[]): GroupField[] =>
  groupList.filter(
    (group) => !(group.standalone === true && group.fields.length === 0),
  )

interface FormBuilderContentProps {
  selectedTemplateItem: SelectedTemplateItem | null
  selectedCategory: string | null
  templateCategories: TemplateCategory[]
  initialFormTitle?: string
  onCategorySelect: (categoryTitle: string) => void
  componentClickRef?: React.MutableRefObject<
    ((component: FormComponentType) => void) | null
  >
  previewToggleRef?: React.MutableRefObject<{
    isPreviewMode: boolean
    togglePreview: () => void
  } | null>
  onStatusChange?: (
    templateName: string,
    categoryTitle: string,
    newStatus: FormTemplateStatus,
  ) => void
  formDataRef?: React.MutableRefObject<
    | (() => {
        formTitle: string
        groups: GroupField[]
        formStatus: FormTemplateStatus
      })
    | null
  >
  savedTemplateFormData?: Record<string, SavedTemplateFormData>
  onFormDataChange?: () => void
  selectedFieldId?: string | null
  onFieldSelect?: (field: FormBuilderSelection | null) => void
  fieldSettingsRef?: React.MutableRefObject<FieldSettingsUpdateHandler | null>
  readOnly?: boolean
}

const FormBuilderContent: React.FC<FormBuilderContentProps> = ({
  selectedTemplateItem,
  selectedCategory,
  templateCategories,
  initialFormTitle,
  onCategorySelect,
  componentClickRef,
  previewToggleRef,
  onStatusChange,
  formDataRef,
  savedTemplateFormData,
  onFormDataChange,
  selectedFieldId,
  onFieldSelect,
  fieldSettingsRef,
  readOnly = false,
}) => {
  const derivedTitle =
    selectedTemplateItem?.name || initialFormTitle || 'Untitled Form'
  const [formTitle, setFormTitle] = useState(derivedTitle)
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false)
  const categoryDropdownRef = useRef<HTMLDivElement>(null)
  const prevTemplateNameRef = useRef<string | null>(null)
  const [groups, setGroups] = useState<GroupField[]>([])
  const groupsRef = useRef(groups)
  groupsRef.current = groups

  useEffect(() => {
    const nextGroups = withoutEmptyStandaloneGroups(groups)
    if (nextGroups.length !== groups.length) {
      setGroups(nextGroups)
    }
  }, [groups])
  const [draggingOver, setDraggingOver] = useState<string | null>(null)
  const [draggedGroupId, setDraggedGroupId] = useState<string | null>(null)
  const [dragOverGroupId, setDragOverGroupId] = useState<string | null>(null)
  // Viewing a saved template shows the rendered form, like the patient sees it.
  const [isPreviewMode, setIsPreviewMode] = useState(readOnly)
  const [previewFieldValues, setPreviewFieldValues] =
    useState<SchemaFormFieldValues>({})
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [formStatus, setFormStatus] = useState<FormTemplateStatus>(
    selectedTemplateItem?.status === FormTemplateStatus.Approved
      ? FormTemplateStatus.Approved
      : selectedTemplateItem?.status === FormTemplateStatus.Draft
        ? FormTemplateStatus.Draft
        : FormTemplateStatus.Pending,
  )
  const markFormDirty = useCallback(() => {
    onFormDataChange?.()
  }, [onFormDataChange])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target as Node)
      ) {
        setCategoryDropdownOpen(false)
      }
    }

    if (categoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [categoryDropdownOpen])

  useEffect(() => {
    setIsPreviewMode(readOnly)
  }, [readOnly])

  useEffect(() => {
    if (!readOnly) return
    setCategoryDropdownOpen(false)
    onFieldSelect?.(null)
  }, [readOnly, onFieldSelect])

  useEffect(() => {
    const status =
      selectedTemplateItem?.status === FormTemplateStatus.Approved
        ? FormTemplateStatus.Approved
        : selectedTemplateItem?.status === FormTemplateStatus.Draft
          ? FormTemplateStatus.Draft
          : FormTemplateStatus.Pending
    setFormStatus(status)
  }, [selectedTemplateItem?.status])

  useEffect(() => {
    setFormTitle(derivedTitle)
  }, [derivedTitle])

  useEffect(() => {
    if (!selectedTemplateItem?.name || !selectedCategory) return
    const key = `${selectedCategory}|${selectedTemplateItem.name}`
    const templateName = selectedTemplateItem.name
    if (savedTemplateFormData?.[key]) {
      prevTemplateNameRef.current = templateName
      return
    }
    const templateChanged = templateName !== prevTemplateNameRef.current
    if (!templateChanged) {
      prevTemplateNameRef.current = templateName
      return
    }
    const defaultGroups = getDefaultGroupsForTemplate(
      selectedCategory,
      templateName,
    )
    if (defaultGroups) setGroups(defaultGroups)
    prevTemplateNameRef.current = templateName
  }, [selectedTemplateItem?.name, selectedCategory, savedTemplateFormData])

  useEffect(() => {
    if (formDataRef) {
      formDataRef.current = () => ({
        formTitle,
        groups,
        formStatus,
      })
    }
  }, [formTitle, groups, formStatus, formDataRef])

  useEffect(() => {
    if (!selectedTemplateItem || !selectedCategory || !savedTemplateFormData) {
      return
    }
    const key = `${selectedCategory}|${selectedTemplateItem.name}`
    const saved = savedTemplateFormData[key]
    if (saved) {
      setFormTitle(saved.formTitle)
      setGroups(saved.groups)
      setFormStatus(
        saved.formStatus === FormTemplateStatus.Approved
          ? FormTemplateStatus.Approved
          : saved.formStatus === FormTemplateStatus.Draft
            ? FormTemplateStatus.Draft
            : FormTemplateStatus.Pending,
      )
    }
  }, [selectedTemplateItem?.name, selectedCategory, savedTemplateFormData])

  const handleCategoryClick = (categoryTitle: string): void => {
    if (readOnly) return
    onCategorySelect(categoryTitle)
    setCategoryDropdownOpen(false)
  }

  const createNewGroup = (): GroupField => {
    return {
      id: createFormBuilderId('group'),
      name: '',
      fields: [],
      required: false,
      autoFill: false,
    }
  }

  const createNewFormRow = (): FormRowField => {
    return {
      id: createFormBuilderId('form-row'),
      type: 'Form Row',
      fields: [],
      columnCount: 2,
    }
  }

  const createStandaloneBlock = (
    componentType: FormComponentType,
  ): { group: GroupField; field: FormField | null } => {
    if (componentType === 'Form Row') {
      return {
        group: {
          id: createFormBuilderId('group'),
          name: '',
          fields: [createNewFormRow()],
          required: false,
          autoFill: false,
          standalone: true,
        },
        field: null,
      }
    }
    const field = createFormField(componentType, createFormBuilderId('field'))
    return {
      group: {
        id: createFormBuilderId('group'),
        name: '',
        fields: [field],
        required: false,
        autoFill: false,
        standalone: true,
      },
      field,
    }
  }

  const appendStandalone = (componentType: FormComponentType): void => {
    if (componentType === 'Group Fields' || componentType === 'Rich Text') {
      return
    }
    const block = createStandaloneBlock(componentType)
    setGroups((prevGroups) => [...prevGroups, block.group])
    if (block.field) onFieldSelect?.(block.field)
  }

  /** Rich Text is always a standalone top-level block (no group title). */
  const createRichTextOnlyGroup = (): GroupField => {
    const newField = createFormField('Rich Text', createFormBuilderId('field'))
    return {
      id: createFormBuilderId('group'),
      name: '',
      fields: [newField],
      required: false,
      autoFill: false,
    }
  }

  const updateNestedGroup = (
    group: GroupField,
    targetId: string,
    updater: (g: GroupField) => GroupField,
  ): GroupField => {
    if (group.id === targetId) {
      return updater(group)
    }
    let changed = false
    const fields = group.fields.map((item) => {
      if ('fields' in item && 'name' in item) {
        const updatedItem = updateNestedGroup(
          item as GroupField,
          targetId,
          updater,
        )
        if (updatedItem !== item) changed = true
        return updatedItem
      }
      return item
    })
    return changed ? { ...group, fields } : group
  }

  /** Recursively update a FormField by fieldId anywhere in the group tree. */
  const updateFieldInGroup = (
    group: GroupField,
    fieldId: string,
    updates: FieldSettingsUpdate,
  ): GroupField => {
    let changed = false
    const fields = group.fields.map((item) => {
      if ('type' in item && item.type === 'Form Row' && 'fields' in item) {
        const row = item as FormRowField
        if (row.id === fieldId && updates.columnCount != null) {
          const columnCount = updates.columnCount === 1 ? 1 : 2
          changed = true
          return {
            ...row,
            columnCount,
            fields: row.fields.slice(0, columnCount),
          }
        }
        let rowChanged = false
        const rowFields = row.fields.map((field) => {
          if (field.id !== fieldId) return field
          rowChanged = true
          return { ...field, ...updates }
        })
        if (!rowChanged) return item
        changed = true
        return {
          ...row,
          fields: rowFields,
        }
      }
      if ('fields' in item && 'name' in item) {
        const updatedItem = updateFieldInGroup(
          item as GroupField,
          fieldId,
          updates,
        )
        if (updatedItem !== item) changed = true
        return updatedItem
      }
      const field = item as FormField
      if (field.id === fieldId) {
        changed = true
        return { ...field, ...updates }
      }
      return item
    })
    return changed ? { ...group, fields } : group
  }

  const updateFieldSettings = useCallback(
    (fieldId: string, updates: FieldSettingsUpdate): void => {
      if (readOnly) return
      markFormDirty()
      setGroups((prevGroups) =>
        prevGroups.map((group) => updateFieldInGroup(group, fieldId, updates)),
      )
    },
    [markFormDirty, readOnly],
  )

  useEffect(() => {
    if (!fieldSettingsRef) return
    fieldSettingsRef.current = updateFieldSettings
    return () => {
      if (fieldSettingsRef.current === updateFieldSettings) {
        fieldSettingsRef.current = null
      }
    }
  }, [fieldSettingsRef, updateFieldSettings])

  const findGroupById = (
    groupList: GroupField[],
    id: string,
  ): GroupField | null => {
    for (const group of groupList) {
      if (group.id === id) return group
      for (const item of group.fields) {
        if (
          item != null &&
          'fields' in item &&
          'name' in item &&
          !('type' in item && (item as FormRowField).type === 'Form Row')
        ) {
          const found = findGroupById([item as GroupField], id)
          if (found) return found
        }
      }
    }
    return null
  }

  const isRichTextOnlyGroup = (g: GroupField): boolean => {
    if (g.fields.length !== 1) return false
    const item = g.fields[0]
    return (
      item != null && 'type' in item && (item as FormField).type === 'Rich Text'
    )
  }

  type FieldSource = {
    field: FormField
    sourceGroupId: string
    formRowId?: string
  } | null

  const findFieldSource = (
    groupList: GroupField[],
    fieldId: string,
  ): FieldSource => {
    for (const group of groupList) {
      if ('fields' in group && 'name' in group) {
        for (const item of group.fields) {
          if ('fields' in item && 'name' in item) {
            const nested = findFieldSource([item as GroupField], fieldId)
            if (nested) return nested
          }
          if ('type' in item && item.type === 'Form Row' && 'fields' in item) {
            const formRow = item as FormRowField
            const found = formRow.fields.find((f) => f.id === fieldId)
            if (found) {
              return {
                field: found,
                sourceGroupId: group.id,
                formRowId: formRow.id,
              }
            }
          }
          if ('type' in item && item.id === fieldId && !('name' in item)) {
            return {
              field: item as FormField,
              sourceGroupId: group.id,
            }
          }
        }
      }
    }
    return null
  }

  const moveFieldToGroup = (fieldId: string, targetGroupId: string): void => {
    const source = findFieldSource(groups, fieldId)
    if (!source || source.sourceGroupId === targetGroupId) return
    markFormDirty()

    setGroups((prevGroups) => {
      let fieldToMove: FormField | null = null
      const removeFromSource = (group: GroupField): GroupField => {
        if (group.id !== source.sourceGroupId) {
          return {
            ...group,
            fields: group.fields.map((item) => {
              if ('fields' in item && 'name' in item) {
                return removeFromSource(item as GroupField)
              }
              return item
            }),
          }
        }
        if (source.formRowId) {
          const updatedFields = group.fields.map((item) => {
            if (
              'type' in item &&
              item.type === 'Form Row' &&
              item.id === source.formRowId
            ) {
              const formRow = item as FormRowField
              const idx = formRow.fields.findIndex((f) => f.id === fieldId)
              if (idx !== -1) {
                fieldToMove = formRow.fields[idx]
                return {
                  ...formRow,
                  fields: formRow.fields.filter((f) => f.id !== fieldId),
                }
              }
            }
            return item
          })
          return { ...group, fields: updatedFields }
        }
        const idx = group.fields.findIndex(
          (f) => 'id' in f && f.id === fieldId && !('name' in f),
        )
        if (idx !== -1) {
          fieldToMove = group.fields[idx] as FormField
          return {
            ...group,
            fields: group.fields.filter((_, i) => i !== idx),
          }
        }
        return group
      }
      const withoutField = prevGroups.map(removeFromSource)
      if (!fieldToMove) return prevGroups
      return withoutEmptyStandaloneGroups(
        withoutField.map((group) => {
          if (group.id === targetGroupId) {
            return {
              ...group,
              fields: [...group.fields, fieldToMove as FormField],
            }
          }
          return group
        }),
      )
    })
  }

  const addComponentToGroup = (
    groupId: string,
    componentType: FormComponentType,
    parentGroupId?: string,
  ): void => {
    markFormDirty()
    if (componentType === 'Group Fields') {
      if (groupId) {
        return
      }

      const newGroup = createNewGroup()
      setGroups((prevGroups) => [...prevGroups, newGroup])
      return
    }

    if (componentType === 'Rich Text') {
      const newGroup = createRichTextOnlyGroup()
      setGroups((prevGroups) => [...prevGroups, newGroup])
      onFieldSelect?.(newGroup.fields[0] as FormField)
      return
    }

    if (componentType === 'Form Row') {
      const newFormRow = createNewFormRow()

      if (parentGroupId || groupId) {
        const targetGroupId = parentGroupId || groupId
        setGroups((prevGroups) =>
          prevGroups.map((group) => {
            if (group.id === targetGroupId) {
              return {
                ...group,
                fields: [...group.fields, newFormRow],
              }
            }
            return updateNestedGroup(group, targetGroupId, (g) => ({
              ...g,
              fields: [...g.fields, newFormRow],
            }))
          }),
        )
      } else {
        appendStandalone('Form Row')
      }
      return
    }

    const newField = createFormField(
      componentType,
      createFormBuilderId('field'),
    )

    const targetGroupId = parentGroupId || groupId
    setGroups((prevGroups) =>
      prevGroups.map((group) => {
        if (group.id === targetGroupId) {
          return { ...group, fields: [...group.fields, newField] }
        }
        return updateNestedGroup(group, targetGroupId, (g) => ({
          ...g,
          fields: [...g.fields, newField],
        }))
      }),
    )
    onFieldSelect?.(newField)
  }

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    groupId?: string,
  ): void => {
    e.preventDefault()
    e.stopPropagation()
    if (readOnly) return
    markFormDirty()
    setDraggingOver(null)

    const targetGroupId = groupId || e.currentTarget.dataset.groupId
    const parentGroupId = e.currentTarget.dataset.parentGroupId

    if (e.dataTransfer.types.includes('application/json')) {
      try {
        const data = JSON.parse(e.dataTransfer.getData('application/json'))
        const componentType = data.type as FormComponentType

        if (componentType === 'Group Fields') {
          if (targetGroupId) {
            return
          }
          const newGroup = createNewGroup()
          setGroups((prevGroups) => [...prevGroups, newGroup])
          return
        }

        if (targetGroupId) {
          const targetGroup = findGroupById(groupsRef.current, targetGroupId)
          if (targetGroup && isRichTextOnlyGroup(targetGroup)) {
            if (componentType === 'Rich Text') {
              const newGroup = createRichTextOnlyGroup()
              setGroups([...groups, newGroup])
              onFieldSelect?.(newGroup.fields[0] as FormField)
            } else {
              appendStandalone(componentType)
            }
          } else if (targetGroup?.standalone === true) {
            appendStandalone(componentType)
          } else {
            addComponentToGroup(targetGroupId, componentType, parentGroupId)
          }
        } else if (componentType === 'Rich Text') {
          const newGroup = createRichTextOnlyGroup()
          setGroups((prevGroups) => [...prevGroups, newGroup])
          onFieldSelect?.(newGroup.fields[0] as FormField)
        } else {
          appendStandalone(componentType)
        }
      } catch (error) {
        logger.error('Error parsing drag data:', error)
      }
      return
    }

    if (
      e.dataTransfer.types.includes('text/plain') &&
      e.dataTransfer.getData('drag-type') !== 'group' &&
      targetGroupId
    ) {
      const fieldId = e.dataTransfer.getData('text/plain')
      if (fieldId) {
        moveFieldToGroup(fieldId, targetGroupId)
      }
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    if (readOnly) return
    const groupId = e.currentTarget.dataset.groupId
    if (groupId) {
      setDraggingOver(groupId)
    } else {
      setDraggingOver('main')
    }
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    if (readOnly) return
    const relatedTarget = e.relatedTarget as HTMLElement
    if (
      !e.currentTarget.contains(relatedTarget) ||
      !relatedTarget?.closest('.form-drop-zone, .group-drop-target')
    ) {
      setDraggingOver(null)
    }
  }

  const handleComponentClick = (componentType: FormComponentType): void => {
    if (readOnly) return
    if (componentType === 'Group Fields') {
      markFormDirty()
      setGroups((prevGroups) => [...prevGroups, createNewGroup()])
      return
    }

    if (componentType === 'Rich Text') {
      markFormDirty()
      const newGroup = createRichTextOnlyGroup()
      setGroups((prevGroups) => [...prevGroups, newGroup])
      onFieldSelect?.(newGroup.fields[0] as FormField)
      return
    }

    markFormDirty()
    appendStandalone(componentType)
  }

  if (componentClickRef) {
    componentClickRef.current = handleComponentClick
  }

  useEffect(() => {
    if (!componentClickRef) return
    componentClickRef.current = handleComponentClick
    return () => {
      if (componentClickRef.current === handleComponentClick) {
        componentClickRef.current = null
      }
    }
  }, [componentClickRef, handleComponentClick])

  const togglePreview = (): void => {
    setIsTransitioning(true)
    setTimeout(() => {
      setIsPreviewMode((prev) => !prev)
      setTimeout(() => {
        setIsTransitioning(false)
      }, 300)
    }, 50)
  }

  useEffect(() => {
    if (previewToggleRef) {
      previewToggleRef.current = {
        isPreviewMode,
        togglePreview,
      }
    }
  }, [isPreviewMode, previewToggleRef])

  const handleGroupUpdate = (updatedGroup: GroupField): void => {
    if (readOnly) return
    markFormDirty()
    setGroups((prevGroups) =>
      withoutEmptyStandaloneGroups(
        prevGroups.map((group) =>
          group.id === updatedGroup.id ? updatedGroup : group,
        ),
      ),
    )
  }

  const handleGroupDelete = (groupId: string): void => {
    if (readOnly) return
    markFormDirty()
    setGroups((prevGroups) =>
      prevGroups.filter((group) => group.id !== groupId),
    )
  }

  const handleGroupCopy = (group: GroupField): void => {
    if (readOnly) return
    markFormDirty()
    const copiedGroup: GroupField = {
      ...group,
      id: createFormBuilderId('group'),
      name: `${group.name} (Copy)`,
      fields: group.fields.map((field) => ({
        ...field,
        id: createFormBuilderId('field'),
      })),
    }
    setGroups([...groups, copiedGroup])
  }

  const handleFieldAdd = (groupId: string, field: FormField): void => {
    if (readOnly) return
    markFormDirty()
    setGroups((prevGroups) =>
      prevGroups.map((group) =>
        group.id === groupId
          ? { ...group, fields: [...group.fields, field] }
          : group,
      ),
    )
  }

  const handleFieldUpdate = (
    groupId: string,
    fieldId: string,
    label: string,
  ): void => {
    if (readOnly) return
    markFormDirty()
    setGroups((prevGroups) =>
      prevGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              fields: group.fields.map((field) =>
                field.id === fieldId ? { ...field, label } : field,
              ),
            }
          : group,
      ),
    )
  }

  const handleFieldDelete = (
    groupId: string,
    fieldId: string,
    parentGroupId?: string,
  ): void => {
    if (readOnly) return
    markFormDirty()
    if (selectedFieldId === fieldId) {
      onFieldSelect?.(null)
    }
    const targetGroupId = parentGroupId || groupId
    setGroups((prevGroups) =>
      withoutEmptyStandaloneGroups(
        prevGroups.map((group) => {
          if (group.id === targetGroupId) {
            return {
              ...group,
              fields: group.fields.filter((item) => item.id !== fieldId),
            }
          }
          return updateNestedGroup(group, targetGroupId, (g) => ({
            ...g,
            fields: g.fields.filter((item) => item.id !== fieldId),
          }))
        }),
      ),
    )
  }

  const handleFieldReorder = (
    groupId: string,
    fieldId: string,
    newIndex: number,
    parentGroupId?: string,
  ): void => {
    if (readOnly) return
    markFormDirty()
    setGroups((prevGroups) =>
      prevGroups.map((group) => {
        const targetId = parentGroupId || groupId
        if (group.id !== targetId) {
          return updateNestedGroup(group, targetId, (g) => {
            const fields = [...g.fields]
            const currentIndex = fields.findIndex((f) => f.id === fieldId)
            if (currentIndex === -1) return g

            const [movedField] = fields.splice(currentIndex, 1)
            fields.splice(newIndex, 0, movedField)

            return { ...g, fields }
          })
        }

        const fields = [...group.fields]
        const currentIndex = fields.findIndex((f) => f.id === fieldId)
        if (currentIndex === -1) return group

        const [movedField] = fields.splice(currentIndex, 1)
        fields.splice(newIndex, 0, movedField)

        return { ...group, fields }
      }),
    )
  }

  const handleNestedGroupUpdate = (
    parentGroupId: string,
    nestedGroupId: string,
    updatedGroup: GroupField,
  ): void => {
    if (readOnly) return
    markFormDirty()
    setGroups((prevGroups) =>
      prevGroups.map((group) =>
        updateNestedGroup(group, parentGroupId, (g) => ({
          ...g,
          fields: g.fields.map((item) =>
            'fields' in item && item.id === nestedGroupId ? updatedGroup : item,
          ),
        })),
      ),
    )
  }

  const handleNestedGroupDelete = (
    parentGroupId: string,
    nestedGroupId: string,
  ): void => {
    if (readOnly) return
    markFormDirty()
    setGroups((prevGroups) =>
      prevGroups.map((group) => {
        if (group.id === parentGroupId) {
          return {
            ...group,
            fields: group.fields.filter((item) => item.id !== nestedGroupId),
          }
        }
        return updateNestedGroup(group, parentGroupId, (g) => ({
          ...g,
          fields: g.fields.filter((item) => item.id !== nestedGroupId),
        }))
      }),
    )
  }

  const handleGroupAdd = (groupId: string, nestedGroup: GroupField): void => {
    if (readOnly) return
    markFormDirty()
    setGroups((prevGroups) =>
      prevGroups.map((group) =>
        updateNestedGroup(group, groupId, (g) => ({
          ...g,
          fields: [...g.fields, nestedGroup],
        })),
      ),
    )
  }

  const handleGroupDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    groupId: string,
  ): void => {
    setDraggedGroupId(groupId)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', groupId)
    e.dataTransfer.setData('drag-type', 'group')
    e.currentTarget.classList.add('dragging-group')
  }

  const handleGroupDragEnd = (e: React.DragEvent<HTMLDivElement>): void => {
    e.currentTarget.classList.remove('dragging-group')
    setDraggedGroupId(null)
    setDragOverGroupId(null)
  }

  const handleGroupDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    groupId: string,
  ): void => {
    if (!draggedGroupId) {
      return
    }
    e.preventDefault()
    e.stopPropagation()
    if (draggedGroupId !== groupId) {
      setDragOverGroupId(groupId)
    }
  }

  const handleGroupDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetGroupId: string,
  ): void => {
    if (!draggedGroupId) {
      return
    }
    e.preventDefault()
    e.stopPropagation()
    if (draggedGroupId !== targetGroupId) {
      const draggedIndex = groups.findIndex((g) => g.id === draggedGroupId)
      const targetIndex = groups.findIndex((g) => g.id === targetGroupId)

      if (draggedIndex !== -1 && targetIndex !== -1) {
        const newGroups = [...groups]
        const [movedGroup] = newGroups.splice(draggedIndex, 1)
        newGroups.splice(targetIndex, 0, movedGroup)
        setGroups(newGroups)
      }
    }
    setDraggedGroupId(null)
    setDragOverGroupId(null)
  }

  return (
    <div
      className={`form-content-transition-wrapper ${
        isTransitioning ? 'transitioning' : ''
      } ${isPreviewMode ? 'preview-mode' : 'builder-mode'}`}
    >
      {isPreviewMode ? (
        <>
          <div className="form-preview-container" id="form-preview-container">
            <FormPreview
              groups={groups}
              formTitle={formTitle}
              interactive
              onDraftValuesChange={setPreviewFieldValues}
            />
          </div>
          <div
            id="form-pdf-output"
            className="form-pdf-output-host"
            aria-hidden="true"
          >
            <FormPdfCapture
              groups={groups}
              formTitle={formTitle}
              fieldValues={previewFieldValues}
              readOnlyDisplayMode="print"
            />
          </div>
        </>
      ) : (
        <div className="form-builder-container">
          <div className="form-content-inner">
            <div className="form-content-header">
              <div className="form-content-header-left">
                <input
                  type="text"
                  className="form-title form-title-input"
                  value={formTitle}
                  onChange={(e) => {
                    setFormTitle(e.target.value)
                    markFormDirty()
                  }}
                  placeholder="Untitled Form"
                  aria-label="Form title"
                  readOnly={readOnly}
                />
              </div>
              <div className="form-content-header-right">
                <div
                  className="form-category-dropdown-container"
                  ref={categoryDropdownRef}
                >
                  {readOnly ? (
                    <span
                      className={`form-category-text form-category-text-readonly ${
                        selectedCategory ? '' : 'form-category-text-placeholder'
                      }`}
                    >
                      {selectedCategory || 'Select category'}
                    </span>
                  ) : (
                    <>
                      <span
                        className={`form-category-text ${
                          selectedCategory
                            ? ''
                            : 'form-category-text-placeholder'
                        }`}
                        onClick={() =>
                          setCategoryDropdownOpen(!categoryDropdownOpen)
                        }
                      >
                        {selectedCategory || 'Select category'}
                      </span>
                      <span
                        className="form-category-arrow"
                        aria-hidden="true"
                        onClick={() =>
                          setCategoryDropdownOpen(!categoryDropdownOpen)
                        }
                      />
                      {categoryDropdownOpen && (
                        <div className="form-category-dropdown-menu">
                          {templateCategories.map((category) => (
                            <div
                              key={category.title}
                              className={`form-category-dropdown-item ${
                                selectedCategory === category.title
                                  ? 'selected'
                                  : ''
                              }`}
                              onClick={() =>
                                handleCategoryClick(category.title)
                              }
                            >
                              {category.title}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="form-content-body">
              {groups.length === 0 ? (
                !readOnly && (
                  <div
                    className={`form-drop-zone ${
                      draggingOver === 'main' ? 'drag-over' : ''
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                  >
                    <p className="drop-zone-text">
                      <div className="drop-zone-text-top">Drag or Click</div>
                      <div>components to add and use in this form</div>
                    </p>
                  </div>
                )
              ) : (
                <div className="form-canvas-items">
                  {withoutEmptyStandaloneGroups(groups).map((group) => (
                    <div
                      key={group.id}
                      data-group-id={group.id}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      className={`group-drop-target ${
                        draggingOver === group.id ? 'drag-over' : ''
                      }`}
                    >
                      <GroupFields
                        group={group}
                        selectedFieldId={selectedFieldId}
                        onFieldSelect={onFieldSelect}
                        onUpdate={handleGroupUpdate}
                        onDelete={handleGroupDelete}
                        onCopy={handleGroupCopy}
                        onFieldAdd={handleFieldAdd}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        isDraggingOver={draggingOver === group.id}
                        onFieldUpdate={handleFieldUpdate}
                        onFieldReorder={handleFieldReorder}
                        onFieldDelete={handleFieldDelete}
                        onGroupAdd={handleGroupAdd}
                        onNestedGroupUpdate={handleNestedGroupUpdate}
                        onNestedGroupDelete={handleNestedGroupDelete}
                        onGroupDragStart={handleGroupDragStart}
                        onGroupDragEnd={handleGroupDragEnd}
                        onGroupDragOver={handleGroupDragOver}
                        onGroupDrop={handleGroupDrop}
                        isGroupDragging={draggedGroupId === group.id}
                        isGroupDragOver={dragOverGroupId === group.id}
                        hasGroupBeingDragged={!!draggedGroupId}
                        isViewMode={readOnly}
                      />
                    </div>
                  ))}
                  {!readOnly && (
                    <div
                      className={`form-drop-zone form-drop-zone-between ${
                        draggingOver === 'main' ? 'drag-over' : ''
                      }`}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                    >
                      <p className="drop-zone-text">
                        <div className="drop-zone-text-top">Drag or Click</div>
                        <div>components to add and use in this form</div>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FormBuilderContent
